import React, { useContext, useMemo, useRef, useState } from 'react'
import { createGetSuggestionsFromApiUrl } from '../../crm/shared/field_inputs/HelpMethods';
import ItemsAppender from '../../crm/shared/field_inputs/item_list/ItemsAppender';
import { bookingStatusOptions } from '../shared/translations';
import { DataContext as SharedDataContext } from '../../../stores/crm-store';
import { CheckBookingAvailability, HandleUpdateBookings } from '../requests/BookingRequests';
import EditCourseBookingsModal from './edit-course-bookings-modal';
import BookingsList from './bookings-list';
import BookingStatusDropdown from './booking-status-dropdown';
import Checkbox from '../../Checkbox';
import { useTranslation } from 'react-i18next';
import { EDIT_COURSE_BOOKINGS_FULL_COURSE_WARN, EDIT_COURSE_BOOKINGS_LABEL, EDIT_COURSE_BOOKINGS_NEW_PARTICIPANT, EDIT_COURSE_BOOKINGS_OL_BOOKING_WARN } from '../../../i18n/keyConstants';

export default function EditCourseBookings({ course, open, onClose, refreshCourseData }) {
    const { t } = useTranslation(['editCourseBookings']);
    const [existingBookings, setExistingBookings] = useState(() =>
        course.bookings.map((booking) => ({
            ...booking,
            isEdited: false,
            isDeleted: false,
            marked: false,
        }))
    );

    const filteredExistingBookings = existingBookings.filter((existingBooking) =>
        !existingBooking.isDeleted
    );

    const [allMarkedStatus, setAllMarkedStatus] = useState();
    const [checkAll, setCheckAll] = useState(false);
    const [newBookings, setNewBookings] = useState([]);
    const [saving, setSaving] = useState(false);
    const allCourseLectureTeachers = course.lectures.flatMap((lecture) => {
        return lecture.teachers;
    });

    const allbookingItems = useMemo(() => {
        return [
            ...filteredExistingBookings.map((existingBooking) =>
                ({ name: existingBooking.coaEmpName, coaEmpId: existingBooking.coaEmpId })),
            ...newBookings.map((newBooking) =>
                ({ name: newBooking.name, coaEmpId: newBooking.coaEmpId })),
            ...course.teachers.map((teacher) =>
                ({ name: teacher.name, coaEmpId: teacher.coaEmpId })),
            ...allCourseLectureTeachers.map((teacher) =>
                ({ name: teacher.name, coaEmpId: teacher.coaEmpId }))
        ]
    }, [filteredExistingBookings, newBookings]);


    const { dispatch } = useContext(SharedDataContext);
    const inputRef = useRef();
    const spotsOpen = (filteredExistingBookings.length + newBookings.length) <
        (course.maxNumBookings + course.maxNumReserves);

    const updateBookingStatus = (booking, newStatus) => {
        setExistingBookings((prev) => prev.map((b) =>
            b.id === booking.id ? { ...b, status: newStatus, isEdited: true }
                : b
        ))
    }
    const updateBookingMark = (booking, newMarking) => {
        setExistingBookings((prev) => prev.map((b) =>
            b.id === booking.id ? { ...b, marked: newMarking }
                : b
        ))
    }

    const onMarkedStatusChange = (newStatus) => {
        setAllMarkedStatus(newStatus)
    }

    const onUpdateMarkedClicked = () => {
        const arr = existingBookings.map((b) =>
            b.marked
                ? { ...b, status: allMarkedStatus, isEdited: true, marked: false }
                : b);

        setExistingBookings(arr);
        setAllMarkedStatus();
        setCheckAll(false);
    }

    const deleteExistingBooking = (idToDelete) => {
        setExistingBookings((prev) => prev.map((b) =>
            b.coaEmpId === idToDelete ? ({ ...b, isDeleted: true })
                : b
        ))
    }

    const checkAllClicked = (val) => {
        const arr = existingBookings.map((b) => true ? { ...b, marked: val } : b);
        setExistingBookings(arr);
        setCheckAll(val);
    }

    const addNewBooking = async (items) => {
        // only new items from suggestions/ItemsAppender have the property # id #
        const newItem = items.find((item) => item?.id);

        // Check if there is a booking in the original existingBookings state with the newItem id
        // with the isDeleted property set to true. We then want to reactivate that booking.
        if (existingBookings.find((b) => b.coaEmpId === newItem.id)) {
            setExistingBookings((prev) =>
                prev.map((pb) => ({ ...pb, isDeleted: false, status: bookingStatusOptions.Reserve.value }))
            )
        } else {
            const availabilityResponse = await CheckBookingAvailability(course.id, newItem.id);

            if (availabilityResponse?.available) {
                setNewBookings((prev) => [...prev, { coaEmpId: newItem.id, name: newItem.name }])
            } else {
                dispatch({
                    type: 'SHOW_GENERIC_EXL_ERROR_TOAST',
                    payload: t(EDIT_COURSE_BOOKINGS_OL_BOOKING_WARN, { name: newItem.name })
                })
            }
        }
    }

    const deleteNewBooking = (idToDelete) => {
        setNewBookings((prev) => prev.filter((prevBooking) => prevBooking.coaEmpId !== idToDelete))
    }

    const handleSave = async () => {
        setSaving(true);
        const response = await HandleUpdateBookings(course.id, existingBookings, newBookings);
        setSaving(false);

        if (response.success) {
            dispatch({ type: 'SHOW_SAVED_TOAST' });
            onClose();
            refreshCourseData();
        } else {
            dispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    return (
        <EditCourseBookingsModal
            open={open}
            onClose={onClose}
            title={t(EDIT_COURSE_BOOKINGS_LABEL)}
            onSave={handleSave}
            isSaving={saving}
        >
            <h6 style={{ margin: "16px 0px 8px 0px" }}>{t(EDIT_COURSE_BOOKINGS_NEW_PARTICIPANT)}</h6>
            {spotsOpen && (
                <div className='crm-editable-field-wrapper'>
                    <div className='input-wrapper'>
                        <ItemsAppender
                            items={allbookingItems}
                            setItems={(items) => addNewBooking(items)}
                            inputRef={inputRef}
                            setIsLoading={setSaving}
                            getSuggestions={createGetSuggestionsFromApiUrl('/api/employee/getEmployeeSuggestions')}
                        />
                    </div>
                </div>
            )}

            {!spotsOpen && (
                <div className='text-secondary' style={{ fontSize: "14px" }}>
                    {t(EDIT_COURSE_BOOKINGS_FULL_COURSE_WARN)}
                </div>
            )}
            <div
                style={{
                    flex: "0 0 15%",
                    paddingTop: "30px",
                    paddingBottom: "30px",
                    borderBottom: "1px solid lightgrey",
                }}>
                <Checkbox
                    setValue={(val) => checkAllClicked(val)}
                    value={checkAll}
                />
            </div>
            <BookingsList
                newBookings={newBookings}
                deleteNewBooking={deleteNewBooking}
                filteredExistingBookings={filteredExistingBookings}
                deleteExistingBooking={deleteExistingBooking}
                updateBookingStatus={updateBookingStatus}
                updateBookingMark={updateBookingMark}

                course={course}
            />

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    borderTop: "1px solid #dee2e6",
                    paddingTop: "16px",
                    marginTop: "10px",
                    gap: "16px"
                }}
            >
                <div style={{ display: "flex", alignItems: "center" }}>
                    <label style={{ whiteSpace: "nowrap", marginRight: "120px" }}>
                        Alla markerade
                    </label>
                    <div style={{ width: "120px" }}>
                        <BookingStatusDropdown
                            onChange={(option) => setAllMarkedStatus(option.value)}
                            value={allMarkedStatus}
                            placeholder="V�lj"
                        />
                    </div>
                </div>
                <button
                    type="button"
                    className="exl-button btn btn-info"
                    onClick={() => onUpdateMarkedClicked()}
                    style={{ height: "38px" }}
                >
                    Uppdatera
                </button>
            </div>


        </EditCourseBookingsModal>
    )
}
